import { MetaHandler } from "@/main/common/MetaHandler";

export type SensitivePreferenceMode = "show" | "blur" | "hide";

export type SensitiveMaskingConfig = {
    sensitivePreference: SensitivePreferenceMode;
    hideSensitiveMedia: boolean;
};

/**
 * A page-level clamp on how a *nested* item may be masked.
 *
 * `sensitivePreferenceFloor` is deliberately narrowed to `"blur"`: the only thing a page has any business
 * asking for is "do not HIDE the items inside me, blur them instead" — a viewer on HIDE who deliberately
 * opened a sensitive page should still see its related items rather than have them silently disappear.
 * It is a floor, never a ceiling, so it can only make masking *weaker*. Widening this back to
 * `SensitivePreferenceMode` is what allowed a page to clamp an explicit SHOW down to BLUR.
 */
export type SensitiveMaskingDirective = {
    disableSensitiveMasking?: boolean;
    sensitivePreferenceFloor?: "blur";
};

export function normalizeSensitivePreference(value: unknown, fallback: SensitivePreferenceMode = "blur"): SensitivePreferenceMode {
    if (typeof value !== "string") {
        return fallback;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized === "show" || normalized === "blur" || normalized === "hide") {
        return normalized;
    }
    return fallback;
}

export function getSensitivePreferenceMode(): SensitivePreferenceMode {
    return normalizeSensitivePreference(MetaHandler.getSensitivePreference(), "blur");
}

export function getHideSensitiveMediaPreference(): boolean {
    return getSensitivePreferenceMode() !== "show";
}

export function resolveSensitiveMaskingConfig(
    defaultPreference: SensitivePreferenceMode,
    {
        disableSensitiveMasking = false,
        sensitivePreferenceFloor,
    }: SensitiveMaskingDirective = {},
): SensitiveMaskingConfig {
    if (disableSensitiveMasking) {
        return {
            sensitivePreference: "show",
            hideSensitiveMedia: false,
        };
    }

    const viewerPreference = normalizeSensitivePreference(defaultPreference, "blur");

    // The floor only ever loosens HIDE into BLUR. A viewer on SHOW or BLUR keeps exactly what they chose —
    // the page does not get to tighten it.
    const sensitivePreference = sensitivePreferenceFloor && viewerPreference === "hide"
        ? "blur"
        : viewerPreference;

    return {
        sensitivePreference,
        hideSensitiveMedia: sensitivePreference !== "show",
    };
}

export function shouldMaskSensitiveMedia(
    isSensitive: boolean,
    preferenceOrHideSensitiveMedia: SensitivePreferenceMode | boolean = getSensitivePreferenceMode(),
): boolean {
    const preference = typeof preferenceOrHideSensitiveMedia === "boolean"
        ? (preferenceOrHideSensitiveMedia ? "blur" : "show")
        : preferenceOrHideSensitiveMedia;
    return Boolean(isSensitive) && preference !== "show";
}
